// Plan-VoiceWidgetRevisions Item 2 (2026-05-25) — voice-mode cards panel.
//
// CHANGED: this panel now renders ONLY the compact card list. Opening an
// article ("Read") no longer renders the full article INSIDE this panel
// (which used to be a `w-3/5` side panel that shrank the avatar to `w-2/5`,
// crowding the avatar's face during a live call). Instead it bubbles the
// slug up via `onReadArticle`; the parent VoicePlayground renders the
// article as a docked column beside a FULL-size avatar (desktop+embedded)
// or a full-screen overlay (mobile / preview). See ArticleDockPanel.tsx.
//
// Cards are kept compact so the avatar face stays clear during the call:
//   - Desktop: a narrow fixed-width column beside the avatar.
//   - Mobile: a short bottom sheet (≈40vh, was 60vh) so the avatar's
//     upper body stays visible underneath.
//
// PROTECTED feature #2 (article cards / HELP_CENTER_CARD) preserved: cards
// still parse + render; "Read" still opens the article (now via the
// parent). Only the FRAMING of the opened article changed.

import React from 'react';
import { X } from 'lucide-react';
import { VoiceDockSegment } from '../../utils/helpCenterBlocks';
import HelpCenterCard from '../Widget/HelpCenterCard';
import ProductCard from '../Widget/ProductCard';
import OrderCard from '../Widget/OrderCard';
import CheckoutLink from '../Widget/CheckoutLink';
import LookCard from '../Widget/LookCard';

interface VoiceContentPanelProps {
    content: VoiceDockSegment[];
    brandColor: string;
    theme: 'light' | 'dark';
    onClose: () => void;
    /** Bubble a card's "Read" up to VoicePlayground, which renders the
     *  article docked-beside (desktop+embedded) or overlay (mobile/preview)
     *  instead of replacing this panel. */
    onReadArticle: (slug: string) => void;
}

const VoiceContentPanel: React.FC<VoiceContentPanelProps> = ({
    content,
    brandColor,
    theme,
    onClose,
    onReadArticle,
}) => {
    const isDark = theme === 'dark';

    const renderCards = (keyPrefix: string) => (
        <div className="p-3 space-y-2">
            {content.map((seg, idx) => {
                if (seg.type === 'card') {
                    return (
                        <HelpCenterCard
                            key={`${keyPrefix}-${idx}`}
                            article_id={seg.data.article_id}
                            title={seg.data.title}
                            description={seg.data.description}
                            slug={seg.data.slug}
                            url={seg.data.url}
                            thumbnail={seg.data.thumbnail}
                            brandColor={brandColor}
                            isDark={isDark}
                            onReadClick={(slug) => onReadArticle(slug)}
                        />
                    );
                }
                if (seg.type === 'page') {
                    return (
                        <HelpCenterCard
                            key={`${keyPrefix}-${idx}`}
                            article_id={seg.data.article_id}
                            title={seg.data.title}
                            description=""
                            slug={seg.data.slug}
                            url={seg.data.url}
                            brandColor={brandColor}
                            isDark={isDark}
                            onReadClick={(slug) => onReadArticle(slug)}
                        />
                    );
                }
                if (seg.type === 'product_card') {
                    return (
                        <ProductCard
                            key={`${keyPrefix}-${idx}`}
                            {...seg.data}
                            brandColor={brandColor}
                            isDark={isDark}
                        />
                    );
                }
                if (seg.type === 'order_card') {
                    return (
                        <OrderCard
                            key={`${keyPrefix}-${idx}`}
                            data={seg.data}
                            brandColor={brandColor}
                            isDark={isDark}
                        />
                    );
                }
                if (seg.type === 'checkout_link') {
                    return (
                        <CheckoutLink
                            key={`${keyPrefix}-${idx}`}
                            {...seg.data}
                            isDark={isDark}
                        />
                    );
                }
                if (seg.type === 'look_card') {
                    return (
                        <LookCard
                            key={`${keyPrefix}-${idx}`}
                            data={seg.data}
                            brandColor={brandColor}
                            isDark={isDark}
                        />
                    );
                }
                if (seg.type === 'media') {
                    const md = seg.data;
                    if (md.type === 'image') {
                        return <img key={`${keyPrefix}-${idx}`} src={md.url} alt={md.alt || ''} className="max-w-full h-auto rounded-lg" />;
                    }
                    if (md.type === 'video') {
                        return (
                            <video
                                key={`${keyPrefix}-${idx}`}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full max-w-full rounded-lg bg-black"
                            >
                                <source src={md.url} />
                            </video>
                        );
                    }
                }
                return null;
            })}
        </div>
    );

    const hasProducts = content.some(s => s.type === 'product_card');
    const hasArticles = content.some(s => s.type === 'card' || s.type === 'page');
    const hasOrders = content.some(s => s.type === 'order_card');
    const hasLooks = content.some(s => s.type === 'look_card');
    // Count how many distinct content families are present; if more than one,
    // fall back to the generic "Related Items" title.
    const familyCount = [hasProducts, hasArticles, hasOrders, hasLooks].filter(Boolean).length;
    const headerLabel =
        familyCount > 1 ? 'Related Items' :
        hasLooks ? 'Your Look' :
        hasOrders ? 'Your Orders' :
        hasProducts ? 'Products' :
        'Related Articles';

    const header = (
        <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <span className="inline-block w-1.5 h-4 rounded-full mr-2 align-middle" style={{ backgroundColor: brandColor }} />
                {headerLabel}
            </h3>
            <button
                onClick={onClose}
                className={`p-1 rounded-md transition-colors ${
                    isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
                aria-label={`Close ${headerLabel.toLowerCase()}`}
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );

    return (
        <>
            {/* Desktop: compact card column beside the avatar (was w-3/5;
                now a narrow fixed width so the avatar face stays clear). */}
            <div className={`hidden sm:flex flex-col w-72 flex-shrink-0 h-full border-l overflow-hidden ${
                isDark ? 'bg-gray-950 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                {header}
                <div className="flex-1 overflow-y-auto">
                    {renderCards('vc')}
                </div>
            </div>

            {/* Mobile: short bottom sheet (≈40vh, was 60vh) so the avatar's
                upper body stays visible above it. */}
            <div className={`sm:hidden fixed inset-x-0 bottom-0 z-40 flex flex-col rounded-t-2xl shadow-2xl ${
                isDark ? 'bg-gray-950' : 'bg-white'
            }`} style={{ maxHeight: '40vh' }}>
                {/* Drag handle — tap to dismiss (mobile has no other close
                    affordance; calls the same `onClose` as the X so the panel
                    can be re-opened by the next `voice_cards` push). */}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={`Close ${headerLabel.toLowerCase()}`}
                    className="flex justify-center pt-2 pb-1 flex-shrink-0 w-full"
                >
                    <div className={`w-10 h-1 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
                </button>
                {header}
                <div className="flex-1 overflow-y-auto">
                    {renderCards('vcm')}
                </div>
            </div>
        </>
    );
};

export default VoiceContentPanel;
